// src/App.tsx
import React, { useState } from 'react';
import { Header } from './components/Header';
import { RankingList } from './components/RankingList';
import { SelectionModal } from './components/SelectionModal';
import { ParticipantDetails } from './components/ParticipantDetails';
import { SubmissionForm } from './components/SubmissionForm';
import { SubmissionRecap } from './components/SubmissionRecap';
import { eurovisionParticipants, events, Participant } from './data/eurovisionData';
import { saveSubmission, Submission } from './utils/storage';

// Set the active event ID here - change this value to switch events
const ACTIVE_EVENT_ID = 1; // Change to 2 for the second event, etc.
const SHEETDB_API_URL = 'https://sheetdb.io/api/v1/us8uph2qqslqr'; // Your SheetDB endpoint

function App() {
  // Find the active event
  const activeEvent = events.find(e => e.id === ACTIVE_EVENT_ID) || events[0];
  
  // Initialize slots based on the active event's maxRankings
  const [rankingList, setRankingList] = useState<(Participant | null)[]>(
    Array(activeEvent.maxRankings).fill(null)
  );
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSlotIndex, setActiveSlotIndex] = useState<number | null>(null);
  const [detailsParticipant, setDetailsParticipant] = useState<Participant | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Submission-related state
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null);
  
  // Open modal for adding/replacing participant
  const handleAddToSlot = (index: number) => {
    setActiveSlotIndex(index);
    setIsModalOpen(true);
  };
  
  // Handle participant selection from modal
  const handleSelectParticipant = (participant: Participant) => {
    if (activeSlotIndex !== null) {
      const newList = [...rankingList];
      newList[activeSlotIndex] = participant;
      setRankingList(newList);
      setIsModalOpen(false);
    }
  };
  
  // Remove participant from ranking
  const removeFromRanking = (indexOrId: number) => {
    console.log("removeFromRanking called with:", indexOrId);
    
    // If it's a small number, treat it as an index directly
    if (indexOrId < rankingList.length) {
      console.log("Treating as direct index:", indexOrId);
      const newList = [...rankingList];
      newList[indexOrId] = null;
      setRankingList(newList);
      return;
    }

    // Otherwise treat it as an ID
    const indexById = rankingList.findIndex(item => item && item.id === indexOrId);
    if (indexById !== -1) {
      console.log("Found item by ID at index:", indexById);
      const newList = [...rankingList];
      newList[indexById] = null;
      setRankingList(newList);
    } else {
      console.warn("Could not find item to remove:", indexOrId);
    }
  };
  
  // Handle reordering (only for filled slots)
  const handleReorder = (oldIndex: number, newIndex: number) => {
    const newList = [...rankingList];
    const [removed] = newList.splice(oldIndex, 1);
    newList.splice(newIndex, 0, removed);
    console.log('Reordered list:', newList);
    setRankingList(newList);
  };
  
  // Show details from ranking
  const showParticipantDetails = (participant: Participant) => {
    setDetailsParticipant(participant);
    setShowDetailsModal(true);
  };
  
  // Handle form submission - updated to work without country and send to SheetDB
  const handleSubmitRanking = async (email: string, username: string) => { // Make async
    // Validate that there's at least one participant in the ranking
    const filledRankings = rankingList.filter(item => item !== null) as Participant[]; // Assert as Participant[]
    if (filledRankings.length === 0) {
      alert('Please add at least one participant to your ranking before submitting');
      return;
    }

    // 1. Prepare data for saving (email, username, ranking, event)
    // Note: id and timestamp will be added by saveSubmission
    const submissionDataToSave = {
      email,
      username,
      country: '', // Empty string since we removed the country field
      ranking: rankingList,
      event: activeEvent
    };

    // 2. Save to localStorage (this adds id and timestamp)
    const savedSubmission = saveSubmission(submissionDataToSave);
    console.log('Submission saved to localStorage:', savedSubmission);

    // 3. Prepare data for SheetDB
    const sheetDbData = {
        email: email,
        username: username, // Added username to the payload as well
        rank1: filledRankings[0]?.country || '', // Use participant country, default to empty string
        rank2: filledRankings[1]?.country || '',
        rank3: filledRankings[2]?.country || '',
        rank4: filledRankings[3]?.country || '',
        rank5: filledRankings[4]?.country || '',
    };

    // 4. Send data to SheetDB
    try {
        console.log('Sending data to SheetDB:', sheetDbData);
        const response = await fetch(SHEETDB_API_URL, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            // SheetDB expects data wrapped in a 'data' object/array if adding single row
            // or just the array if adding multiple rows.
            // Let's try sending the object directly first, common for single additions.
            body: JSON.stringify({ data: sheetDbData }) 
        });

        if (!response.ok) {
          // Log error details if the request failed
          const errorData = await response.json();
          console.error('SheetDB API Error:', response.status, response.statusText, errorData);
          alert(`There was an issue saving your ranking online. Please try again later. Error: ${response.statusText}`);
          // Optional: Decide if you still want to proceed with showing the recap if online save fails
          // return;
        } else {
          const result = await response.json();
          console.log('Successfully saved to SheetDB:', result);
          // Optionally show a success message specific to online save
        }

    } catch (error) {
        console.error('Error sending data to SheetDB:', error);
        alert('An unexpected error occurred while saving your ranking online. Please check your connection and try again.');
        // Optional: Decide if you still want to proceed with showing the recap if online save fails
        // return;
    }


    // 5. Update state to show recap (using the object returned by saveSubmission)
    setCurrentSubmission(savedSubmission);
    setIsSubmitted(true);
  };
  
  // Show the recap screen after submission
  if (isSubmitted && currentSubmission) {
    return (
      <div className="max-w-lg mx-auto p-4">
        <SubmissionRecap
          submission={currentSubmission}
          onCreateNew={() => {
            setIsSubmitted(false);
            setRankingList(Array(activeEvent.maxRankings).fill(null));
            setCurrentSubmission(null);
          }}
        />
      </div>
    );
  }
  
  // Main app view
  return (
    <div className="max-w-lg mx-auto p-4">
      <Header event={activeEvent} />
      
      {/* Main content - Ranking list with empty slots */}
      <div className="w-full">
        <RankingList 
          items={rankingList}
          onAddToSlot={handleAddToSlot}
          onRemove={removeFromRanking}
          onReorder={handleReorder}
          onShowDetails={showParticipantDetails}
        />
      </div>
      
      {/* Submission form */}
      <SubmissionForm 
        rankingList={rankingList}
        onSubmit={handleSubmitRanking}
      />
      
      {/* Modal for participant selection */}
      <SelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        participants={eurovisionParticipants}
        onSelect={handleSelectParticipant}
        onRemove={removeFromRanking}
        alreadySelected={rankingList.filter(p => p !== null).map(p => p!.id)}
        activeSlotIndex={activeSlotIndex}
      />
      
      {/* Separate modal for viewing details from ranking */}
      <ParticipantDetails
        participant={detailsParticipant}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onAddToRanking={() => {}} // Not used anymore
        onRemove={removeFromRanking}
        isAlreadyInRanking={detailsParticipant ? rankingList.some(p => p && p.id === detailsParticipant.id) : false}
      />
    </div>
  );
}

export default App;